import { DialogComponent } from '@theme/dialog';
import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';

/** @param {Element} source @returns {boolean} */
export function protectionSelected(source) {
  const checkbox = source.querySelector('[data-prime-protection-checkbox]');
  return checkbox instanceof HTMLInputElement && !checkbox.disabled && checkbox.checked;
}

/** @param {any} phone @returns {boolean} */
export function protectionEligible(phone) {
  const unitPrice = Number(phone.final_price ?? phone.price);
  return unitPrice > 80000 && phone.product_type === 'Smartphone' &&
    (phone.vendor === 'Samsung' || (phone.vendor === 'Apple' && /\biPhone\b/i.test(phone.product_title || phone.title || '')));
}

/** @param {HTMLElement} source @param {Document} html */
export function updateProtectionForm(source, html) {
  const nextForm = html.querySelector(`product-form-component[data-product-id="${CSS.escape(source.dataset.productId)}"]`);
  if (!nextForm) return;
  source.dataset.primeProtectionEligible = nextForm.dataset.primeProtectionEligible || 'false';
  source.dataset.primePhoneTitle = nextForm.dataset.primePhoneTitle || '';
  const current = source.querySelector('[data-prime-protection-offer]');
  const next = nextForm.querySelector('[data-prime-protection-offer]');
  const selected = protectionSelected(source);
  if (!next) current?.remove();
  else {
    const clone = next.cloneNode(true);
    const checkbox = clone.querySelector('[data-prime-protection-checkbox]');
    if (checkbox instanceof HTMLInputElement) {
      checkbox.checked = selected;
      checkbox.disabled = !controller()?.enabled;
    }
    const expanded = current?.querySelector('details')?.open;
    if (expanded) clone.querySelector('details')?.setAttribute('open', '');
    if (current) current.replaceWith(clone);
    else source.querySelector('[ref="addToCartButtonContainer"]')?.after(clone);
  }
}

/**
 * Counts protection linked to a particular phone, rather than any protection in the cart.
 * @param {{key: string, properties?: Record<string, string>}} phone
 * @param {Array<any>} items
 * @param {string} variantId
 * @returns {number}
 */
export function protectionQuantity(phone, items, variantId) {
  return items.reduce((count, item) => {
    if (String(item.variant_id) !== variantId) return count;
    const properties = item.properties || {};
    const linked = properties._prime_phone_key === phone.key ||
      (phone.properties?._prime_group && properties._prime_group === phone.properties._prime_group);
    return count + (linked ? item.quantity : 0);
  }, 0);
}

/** @returns {PrimeProtectionController | null} */
function controller() {
  return document.querySelector('prime-protection-controller');
}

/**
 * Captures opt-in as part of the native phone add. Both products use one cart request.
 * @param {any} phone
 * @param {HTMLElement} source
 * @param {boolean} [selected]
 * @returns {Array<any>}
 */
export function protectionItems(phone, source, selected = protectionSelected(source)) {
  const owner = controller();
  if (!owner?.enabled || source.dataset.primeProtectionEligible !== 'true') return [phone];
  const group = crypto.randomUUID();
  const linkedPhone = { ...phone, properties: { ...phone.properties, _prime_group: group, _prime_eligible: '1' } };
  if (!selected) return [linkedPhone];
  return [linkedPhone, {
    id: Number(owner.dataset.variantId),
    quantity: phone.quantity,
    properties: owner.protectionProperties({ ...linkedPhone, variant_id: phone.id, product_title: source.dataset.primePhoneTitle }),
  }];
}

/**
 * Converts eligible native form data to the Ajax API's multi-item format.
 * @param {FormData} formData
 * @param {HTMLElement} source
 */
export function prepareProtectionForm(formData, source) {
  if (!controller()?.enabled || source.dataset.primeProtectionEligible !== 'true') return;
  const properties = {};
  for (const [key, value] of formData.entries()) {
    const match = /^properties\[(.+)\]$/.exec(key);
    if (match) properties[match[1]] = value;
  }
  const phone = { id: Number(formData.get('id')), quantity: Number(formData.get('quantity')) || Number(source.dataset.quantityDefault) || 1, properties };
  if (formData.get('selling_plan')) phone.selling_plan = formData.get('selling_plan');
  const items = protectionItems(phone, source);
  for (const key of [...formData.keys()]) {
    if (/^(id|quantity|selling_plan)$/.test(key) || key.startsWith('properties[')) formData.delete(key);
  }
  for (const [index, item] of items.entries()) {
    formData.append(`items[${index}][id]`, String(item.id));
    formData.append(`items[${index}][quantity]`, String(item.quantity));
    if (item.selling_plan) formData.append(`items[${index}][selling_plan]`, item.selling_plan);
    for (const [key, value] of Object.entries(item.properties || {})) formData.append(`items[${index}][properties][${key}]`, String(value));
  }
}

/**
 * @typedef {object} ProtectionRefs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement} phone
 * @property {HTMLElement} actions
 * @property {HTMLButtonElement} add
 * @property {HTMLElement} status
 * @extends {DialogComponent<ProtectionRefs>}
 */
class PrimeProtectionController extends DialogComponent {
  #abort;
  #cartPromise;
  #cart;
  #phoneKey;
  #busy = false;
  #settling = Promise.resolve();
  #offered = new Set();
  #observer;
  #returnDrawer;

  get enabled() { return this.dataset.enabled === 'true' && Boolean(this.dataset.variantId); }

  connectedCallback() {
    super.connectedCallback();
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    document.addEventListener('click', this.#handleInfo, { signal });
    document.addEventListener('change', this.#handleSelection, { signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCart, { signal });
    document.addEventListener(StandardEvents.productSelect, this.#handleVariant, { signal });
    this.refs.dialog.addEventListener('close', () => {
      if (this.#returnDrawer?.isConnected && !this.#returnDrawer.isOpen) this.#returnDrawer.open();
      this.#returnDrawer = null;
    }, { signal });
    this.#observer = new MutationObserver(() => this.#syncOffers());
    this.#observer.observe(document.body, { childList: true, subtree: true });
    this.#syncOffers();
    if (this.enabled) this.fetchCart().then(cart => { this.#cart = cart; this.#syncOffers(); }).catch(() => {});
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    this.#observer?.disconnect();
    this.#cartPromise = null;
  }

  /** @returns {Promise<any>} */
  async fetchCart() {
    if (!this.#cartPromise) {
      this.#cartPromise = fetch(this.dataset.cartUrl, { headers: { Accept: 'application/json' }, signal: this.#abort.signal })
        .then(response => { if (!response.ok) throw new Error(this.dataset.error); return response.json(); })
        .finally(() => { this.#cartPromise = null; });
    }
    return this.#cartPromise;
  }

  /** @param {any} phone @returns {Record<string, string>} */
  protectionProperties(phone) {
    const properties = {
      _prime_phone_variant: String(phone.variant_id),
      [this.dataset.deviceLabel]: phone.product_title || phone.title || '',
      [this.dataset.startLabel]: this.dataset.startValue,
      [this.dataset.termLabel]: this.dataset.termValue,
    };
    if (phone.properties?._prime_group) properties._prime_group = phone.properties._prime_group;
    else properties._prime_phone_key = phone.key;
    return properties;
  }

  #syncOffers() {
    for (const input of document.querySelectorAll('[data-prime-protection-checkbox]')) {
      if (!(input instanceof HTMLInputElement)) continue;
      input.disabled = !this.enabled;
      const form = input.closest('product-form-component');
      const offer = input.closest('[data-prime-protection-offer]');
      const variant = form?.querySelector('input[name="id"]')?.value;
      const existing = this.#cart?.items?.some(item => String(item.variant_id) === this.dataset.variantId && item.properties?._prime_phone_variant === variant);
      const note = offer?.querySelector('[data-prime-protection-existing]');
      if (note) note.hidden = !existing;
      const accelerated = form?.querySelector('[ref="acceleratedCheckoutButtonContainer"]');
      if (accelerated) accelerated.hidden = this.enabled && input.checked;
      const checkoutNote = offer?.querySelector('[data-prime-protection-checkout]');
      if (checkoutNote) checkoutNote.hidden = !(this.enabled && input.checked);
    }
    for (const input of document.querySelectorAll('[data-prime-cart-protection-checkbox]')) {
      if (!(input instanceof HTMLInputElement)) continue;
      const offer = input.closest('[data-prime-protection-offer]');
      const phone = this.#cart?.items?.find(item => item.key === input.dataset.phoneKey);
      const covered = phone ? protectionQuantity(phone, this.#cart.items, this.dataset.variantId) : Number(offer?.dataset.primeCovered || 0);
      const quantity = phone?.quantity || Number(offer?.dataset.primePhoneQuantity || 1);
      input.checked = covered >= quantity;
      input.indeterminate = covered > 0 && covered < quantity;
      input.disabled = !this.enabled || this.#busy || !phone || !protectionEligible(phone);
      if (offer) offer.setAttribute('aria-busy', String(this.#busy));
    }
  }

  #handleSelection = event => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    if (input.matches('[data-prime-protection-checkbox]')) this.#syncOffers();
    if (input.matches('[data-prime-cart-protection-checkbox]')) {
      this.setCartProtection(input.dataset.phoneKey, input.checked).catch(() => {});
    }
  };

  #handleVariant = event => {
    event.promise?.then(() => this.#syncOffers()).catch(() => {});
  };

  #handleInfo = event => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('[data-prime-protection-info]');
    if (!button) return;
    event.preventDefault();
    this.openInformation(button.dataset.phoneKey).catch(() => { this.refs.status.textContent = this.dataset.error; });
  };

  /** @param {string} [phoneKey] */
  async openInformation(phoneKey) {
    if (this.refs.dialog.open) return;
    this.#phoneKey = phoneKey;
    this.refs.status.textContent = '';
    this.refs.actions.hidden = true;
    this.refs.phone.textContent = '';
    if (phoneKey && this.enabled) {
      const cart = await this.fetchCart();
      const phone = cart.items.find(item => item.key === phoneKey);
      if (phone && protectionEligible(phone)) {
        this.refs.phone.textContent = phone.product_title || phone.title;
        this.refs.actions.hidden = protectionQuantity(phone, cart.items, this.dataset.variantId) >= phone.quantity;
      }
    }
    const drawer = document.querySelector('cart-drawer-component')?.closest('theme-drawer');
    this.#returnDrawer = drawer?.isOpen ? drawer : null;
    if (drawer?.isOpen) await drawer.close();
    this.showDialog();
  }

  /** Adds only the uncovered quantity after a fresh cart check. */
  async addProtection() {
    if (!this.enabled || this.#busy || !this.#phoneKey) return;
    this.#busy = true;
    this.refs.add.disabled = true;
    this.refs.status.textContent = '';
    try {
      const cart = await this.fetchCart();
      this.#cart = cart;
      let phone = cart.items.find(item => item.key === this.#phoneKey);
      if (!phone || !protectionEligible(phone)) { await this.closeDialog(); return; }
      const missing = Math.max(0, phone.quantity - protectionQuantity(phone, cart.items, this.dataset.variantId));
      if (missing > 0) {
        phone = await this.#linkPhone(phone);
        const item = { id: Number(this.dataset.variantId), quantity: missing, properties: this.protectionProperties(phone) };
        this.#cart = await this.#mutate(this.dataset.addUrl, { items: [item] }, 'add');
      }
      this.refs.status.textContent = this.dataset.success;
      await this.closeDialog();
    } catch (error) {
      if (error.name !== 'AbortError') this.refs.status.textContent = this.dataset.error;
    } finally {
      this.#busy = false;
      this.refs.add.disabled = false;
      this.#syncOffers();
    }
  }

  /** @param {any} phone @returns {Promise<any>} */
  async #linkPhone(phone) {
    if (phone.properties?._prime_group) return phone;
    const plans = this.#cart.items.filter(item => protectionQuantity(phone, [item], this.dataset.variantId) > 0);
    const group = crypto.randomUUID();
    this.#cart = await this.#mutate(this.dataset.changeUrl, { id: phone.key, quantity: phone.quantity,
      properties: { ...phone.properties, _prime_group: group, _prime_eligible: '1' } }, 'update');
    for (const plan of plans) {
      const properties = { ...plan.properties, _prime_group: group };
      delete properties._prime_phone_key;
      this.#cart = await this.#mutate(this.dataset.changeUrl, { id: plan.key, quantity: plan.quantity, properties }, 'update');
    }
    const linked = this.#cart.items.find(item => String(item.variant_id) !== this.dataset.variantId && item.properties?._prime_group === group);
    if (!linked) throw new Error(this.dataset.error);
    return linked;
  }

  /** @param {string} phoneKey @param {boolean} selected */
  async setCartProtection(phoneKey, selected) {
    if (!this.enabled || this.#busy || !phoneKey) return;
    this.#busy = true;
    this.#syncOffers();
    let statusKey = phoneKey;
    let message = '';
    try {
      this.#cart = await this.fetchCart();
      let phone = this.#cart.items.find(item => item.key === phoneKey);
      if (!phone || !protectionEligible(phone)) return;
      if (selected) {
        const missing = Math.max(0, phone.quantity - protectionQuantity(phone, this.#cart.items, this.dataset.variantId));
        if (missing > 0) {
          phone = await this.#linkPhone(phone);
          statusKey = phone.key;
          this.#cart = await this.#mutate(this.dataset.addUrl, { items: [{ id: Number(this.dataset.variantId), quantity: missing,
            properties: this.protectionProperties(phone) }] }, 'add');
        }
        message = this.dataset.success;
      } else {
        const plans = this.#cart.items.filter(item => protectionQuantity(phone, [item], this.dataset.variantId) > 0);
        for (const plan of plans) this.#cart = await this.#mutate(this.dataset.changeUrl, { id: plan.key, quantity: 0 }, 'remove');
        message = this.dataset.removed;
      }
    } catch (error) {
      if (error.name !== 'AbortError') message = this.dataset.error;
    } finally {
      this.#busy = false;
      this.#syncOffers();
      const status = document.querySelector(`[data-prime-phone-key="${CSS.escape(statusKey)}"] [data-prime-cart-status]`);
      if (status) status.textContent = message;
    }
  }

  /** @param {string} url @param {object} payload @param {string} action */
  async #mutate(url, payload, action) {
    const sections = [...new Set([...document.querySelectorAll('cart-items-component')].map(item => item.dataset.sectionId).filter(Boolean))].slice(0, 5);
    const deferred = CartLinesUpdateEvent.createPromise();
    this.refs.dialog.dispatchEvent(new CartLinesUpdateEvent({ action, context: 'cart', lines: [], promise: deferred.promise }));
    try {
      const response = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ ...payload, sections, sections_url: window.location.pathname }), signal: this.#abort.signal });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || this.dataset.error);
      const cart = data.items && data.token ? data : await this.fetchCart();
      deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { items: cart.items, sections: data.sections, source: 'prime-protection', didError: false } });
      return cart;
    } catch (error) {
      deferred.reject(error);
      if (error.name !== 'AbortError') this.dispatchEvent(new CartErrorEvent({ error: this.dataset.error, code: 'SERVICE_UNAVAILABLE' }));
      throw error;
    }
  }

  #handleCart = event => {
    if (!this.enabled || !event.promise) return;
    this.#settling = this.#settling.then(async () => {
      const { detail } = await event.promise;
      if (!this.isConnected || detail?.didError) return;
      this.#cart = detail?.items ? { items: detail.items } : await this.fetchCart();
      this.#syncOffers();
      if (detail?.source === 'prime-protection') return;
      await this.#trimProtection();
      if (event.action !== 'add') return;
      const ids = new Set((event.lines || []).map(line => String(line.merchandiseId).split('/').pop()));
      const phone = this.#cart.items.find(item => ids.has(String(item.variant_id)) &&
        protectionEligible(item) &&
        protectionQuantity(item, this.#cart.items, this.dataset.variantId) < item.quantity && !this.#offered.has(item.key));
      if (!phone) return;
      this.#offered.add(phone.key);
      const modal = event.target instanceof Element ? event.target.closest('dialog') : null;
      if (modal?.open && modal !== this.refs.dialog) await new Promise(resolve => modal.addEventListener('close', resolve, { once: true, signal: this.#abort.signal }));
      await new Promise(resolve => requestAnimationFrame(resolve));
      if (this.isConnected) await this.openInformation(phone.key);
    }).catch(() => {});
  };

  /** Removes orphan protection and caps it after phone quantity decreases. Never adds paid coverage automatically. */
  async #trimProtection() {
    const CartItems = customElements.get('cart-items-component');
    const pending = CartItems?.pendingQuantityUpdates || [];
    if (pending.length) {
      await Promise.allSettled(pending);
      this.#cart = await this.fetchCart();
    }
    const plans = this.#cart.items.filter(item => String(item.variant_id) === this.dataset.variantId);
    const capacity = new Map();
    for (const plan of plans) {
      const p = plan.properties || {};
      if (!p._prime_group && !p._prime_phone_key) continue;
      const phone = this.#cart.items.find(item => String(item.variant_id) !== this.dataset.variantId &&
        (p._prime_phone_key === item.key || (p._prime_group && p._prime_group === item.properties?._prime_group)));
      const identity = phone?.key || p._prime_group || p._prime_phone_key;
      const available = capacity.has(identity) ? capacity.get(identity) : phone && protectionEligible(phone) ? phone.quantity : 0;
      const allowed = Math.min(plan.quantity, available);
      capacity.set(identity, available - allowed);
      if (allowed !== plan.quantity) this.#cart = await this.#mutate(this.dataset.changeUrl, { id: plan.key, quantity: allowed }, allowed ? 'update' : 'remove');
    }
  }
}

if (!customElements.get('prime-protection-controller')) customElements.define('prime-protection-controller', PrimeProtectionController);
